import { motion } from "framer-motion";
import { MdOutlineFastfood } from "react-icons/md";
import { FoodCategory } from "../../../types";

const Button = ({
  category,
  filter,
  setFilter,
}: {
  category: FoodCategory;
  filter: string;
  setFilter: any;
}) => {
  const isActive = category.urlParam === filter;
  
  return (
    <motion.button
      onClick={() => setFilter(category.urlParam)}
      whileHover={{ 
        scale: 1.05, 
        y: -4,
        boxShadow: isActive 
          ? "0 20px 40px rgba(172, 148, 48, 0.5), 0 0 30px rgba(172, 148, 48, 0.3)"
          : "0 20px 40px rgba(0, 0, 0, 0.15)"
      }}
      whileTap={{ scale: 0.98, y: 0 }}
      transition={{ type: "spring", stiffness: 400, damping: 17 }}
      className={`
        group relative overflow-hidden
        min-w-[140px] px-6 py-4
        rounded-2xl cursor-pointer
        transition-all duration-300 ease-out
        ${
          isActive
            ? "bg-gradient-to-br from-[#ac9430] via-[#b89d35] to-[#227a32] text-white shadow-2xl"
            : "bg-white hover:bg-gradient-to-br hover:from-gray-50 hover:to-white text-gray-700 shadow-lg border-2 border-gray-100 hover:border-[#ac9430]/30"
        }
      `}
    >
      {/* Animated background gradient on hover */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-br from-[#ac9430]/10 to-[#227a32]/10 opacity-0 group-hover:opacity-100"
        initial={false}
        animate={{ opacity: isActive ? 0 : 0 }}
        whileHover={{ opacity: isActive ? 0 : 1 }}
        transition={{ duration: 0.3 }}
      />
      
      {/* Shine effect on active */}
      {isActive && (
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
          animate={{ x: ["-100%", "200%"] }}
          transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
        />
      )}
      
      {/* Content */}
      <div className="relative z-10 flex items-center gap-3">
        {/* Icon container with 3D effect */}
        <motion.div
          whileHover={{ rotate: [0, -10, 10, -10, 0] }}
          transition={{ duration: 0.5 }}
          className={`
            flex-shrink-0 w-11 h-11 rounded-xl
            flex items-center justify-center
            transition-all duration-300
            ${
              isActive
                ? "bg-white/20 backdrop-blur-sm shadow-lg"
                : "bg-gradient-to-br from-[#ac9430]/10 to-[#227a32]/10 group-hover:from-[#ac9430]/20 group-hover:to-[#227a32]/20"
            }
          `}
        >
          <span
            className={`text-2xl transition-all duration-300 ${
              isActive
                ? "text-white drop-shadow-lg"
                : "text-[#ac9430] group-hover:text-[#227a32]"
            }`}
          >
            {category.icon || <MdOutlineFastfood />}
          </span>
        </motion.div>
        
        {/* Text */}
        <p
          className={`
            text-sm font-bold text-left leading-tight tracking-wide
            transition-all duration-300
            ${
              isActive
                ? "text-white drop-shadow-md"
                : "text-gray-700 group-hover:text-[#ac9430]"
            }
          `}
        >
          {category.name}
        </p>
      </div>
      
      {/* Active indicator dot */}
      {isActive && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="absolute top-2 right-2 w-2 h-2 rounded-full bg-white shadow-lg"
        />
      )}
      
      {/* Bottom accent line */}
      <motion.div
        className={`absolute bottom-0 left-0 right-0 h-1 rounded-b-2xl ${
          isActive
            ? "bg-gradient-to-r from-white/40 via-white/60 to-white/40"
            : "bg-gradient-to-r from-[#ac9430]/0 via-[#ac9430]/50 to-[#ac9430]/0"
        }`}
        initial={{ scaleX: 0 }}
        animate={{ scaleX: isActive ? 1 : 0 }}
        whileHover={{ scaleX: 1 }}
        transition={{ duration: 0.3 }}
      />
    </motion.button>
  );
};

export default Button;
